import { globalErrorCondition } from "../../../Helpers/ErrorHelper";
import { endOfDay, format } from 'date-fns';
import { DateFormat, dateFromState } from "../../../Helpers/DateTimeHelper";
import { BOOKINGS_API_URL, BOOKINGS_TABS, BOOKINGS_VIEWS, UPDATE } from '../Bookings';
import { hideModal } from "../../../Redux/GlobalReducer";
import { SEARCHED_AT_SIGN_IN_MODAL } from "../Components/Modals/SearchedAtSignInModal";
import { retrieveMultiFilterValuesByPrefix } from "../../../Helpers/FormHelper";
import { FILTER_PREFIXES } from "../../../Components/Inputs/MultiFilterInput";
import { LOADING } from "../../../Components/Table/TimelineTable";
import {
    bookingCountsLoaded,
    bookingListBookingCancelled,
    bookingListBookingSeriesCancelled,
    bookingListRemoveBookings,
    bookingListUpdated,
    bookingsLoaded,
    setRecordsAreLoading
} from "../Redux/BookingListSlice";
import { categorySummariesLoaded } from "../Redux/LocalReducer";

const buildRequestParameters = (view, filters, dateRangeStart = null, dateRangeEnd = null, sortDir = null, perPage = null, concurrentVisitCount = null, updatedSince = null) => {

    let viewOwn = (view === BOOKINGS_VIEWS.MY_BOOKINGS);
    let {multiFilter, category, amenity, searchTerm, status} = filters;
    let blockIds = retrieveMultiFilterValuesByPrefix(multiFilter, FILTER_PREFIXES.BLOCK);

    // If category is set to 'all', we don't want to send that as a literal
    // string. Just send no parameter
    if (category === 'all') {
        category = '';
    }

    let params = {
        view_own: viewOwn,
        sort_dir: sortDir,
        per_page: perPage,
        ...(blockIds.length && {block_ids: blockIds.toString()}),
        ...(category && {category_slugs: [category].toString()}),
        ...(amenity && {amenity_slugs: [amenity].toString()}),
        ...(searchTerm && {search_term: searchTerm}),
        ...(status && {status}),
        ...(concurrentVisitCount && {infinite_scroll_num_concurrent_visits: concurrentVisitCount}),
        ...(updatedSince && {updated_since: updatedSince})
    }

    if (dateRangeStart) {
        params.date_range_start = dateRangeStart;
    }

    if (dateRangeEnd) {
        params.date_range_end = dateRangeEnd;
    }

    return params;
}

const requestData = async (
    view,
    scope,
    dateRangeStart,
    dateRangeEnd,
    sortDir,
    perPage,
    filters = {},
    concurrentVisitCount = 0,
    updatedSince
) => {
    const params = buildRequestParameters(view, filters, dateRangeStart, dateRangeEnd, sortDir, perPage, concurrentVisitCount, updatedSince);

    try {
        const { data } = await axios.get(`${BOOKINGS_API_URL}booking?type=${scope}`, { params });
        return data;
    } catch (error) {
        globalErrorCondition('Error loading booking data', error);
    }
    return {};
}

export const requestCountsData = async(
    view,
    scope,
    filters = {},
) => {
    const params = buildRequestParameters(view, filters);

    try {
        const { data } = await axios.get(`${BOOKINGS_API_URL}booking-counts?type=${scope}`, { params });
        return data.data;
    } catch (error) {
        globalErrorCondition('Error loading booking data', error);
    }
    return {};
}

/**
 * Get records for the first time. Makes two requests, one going backwards,
 * one forwards
 * @param scope
 * @param view
 * @param selectedCategorySlug
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const getInitialData = (scope, view, selectedCategorySlug = null) => async (dispatch, getState) => {
    let now = format(new Date(), DateFormat.MYSQL)
    let perPage = 20;

    dispatch(setRecordsAreLoading(view, scope, LOADING.AT_START));

    let filters = {};
    if (selectedCategorySlug) {
        filters.category = selectedCategorySlug;
    }

    // Get records going backwards
    let earlierBookings = await requestData(view, scope, null, now, 'desc', 10, filters);
    dispatch( bookingsLoaded(view, scope, earlierBookings, 'desc', false, 0, LOADING.AT_START) );
    dispatch( setRecordsAreLoading(view, scope, LOADING.AT_END) );

    // Get records going forwards
    let futureBookings = await requestData(view, scope, now, null, 'asc', perPage, filters);
    dispatch( bookingsLoaded(view, scope, futureBookings, 'asc', false, perPage, LOADING.AT_END) );

    let counts = await requestCountsData(view, scope)
    dispatch( bookingCountsLoaded(view, scope, counts) );
}

export const getPagedData = (
    tabState,
    laterThan,
    earlierThan,
    loadingType,
    direction,
    concurrentBookingCount = null) => async (dispatch, getState) => {

    const {view, scope} = tabState;

    let state = getState();

    dispatch(
        setRecordsAreLoading(view, scope, loadingType)
    );

    const filters = state.module.BOOKINGS.filters;
    let {dateRangeStart, dateRangeEnd} = filters;

    if (dateRangeStart) {
        laterThan = (laterThan && laterThan > dateRangeStart)
            ? laterThan
            : dateRangeStart;
        earlierThan = (earlierThan && earlierThan < dateRangeEnd)
            ? earlierThan
            : dateRangeEnd;
    }

    let perPage = 20;

    let data = await requestData(view, scope, laterThan, earlierThan, direction, perPage, filters, concurrentBookingCount);

    dispatch(
        bookingsLoaded(view, scope, data, direction, false, perPage)
    );
}

export const getBookingListData = (
    scope,
    view,
    filtersChanged = false
) => async (dispatch, getState) => {
    const state = getState();

    let perPage = 20;
    let direction = (scope === BOOKINGS_TABS.UPCOMING) ? 'asc' : 'desc';

    const filters = state.module.BOOKINGS.filters;
    let { dateRangeStart, dateRangeEnd } = filters;

    try {
        dispatch(setRecordsAreLoading(view, scope, LOADING.NORMAL));

        let data = await requestData(view, scope, dateRangeStart, dateRangeEnd, direction, perPage, filters)

        dispatch( bookingsLoaded(view, scope, data, direction, filtersChanged, perPage) );

        if (filtersChanged) {
            let counts = await requestCountsData(view, scope, filters)
            dispatch( bookingCountsLoaded(view, scope, counts) );
        }
    } catch (error) {
        globalErrorCondition('Error loading bookings data', error);
    }
}

export const getBookingListRefreshData = (view, scope) => async(dispatch, getState) => {
    const state = getState();

    let refreshRecordsToGet = 25;
    let direction = (scope === BOOKINGS_TABS.UPCOMING) ? 'asc' : 'desc';
    let currentBookings = state.module['BOOKINGS'].bookingList[view][scope].bookings;
    let lastUpdated = state.module['BOOKINGS'].bookingList[view][scope].lastUpdated;
    const filters = state.module.BOOKINGS.filters;

    let startDate = null;
    let endDate = null;

    if (Object.keys(currentBookings).length) {
        let bookingsEndDates = Object.values(currentBookings).map(booking => booking.end);

        bookingsEndDates.sort();

        // Determine daterange boundaries to refresh
        startDate = dateFromState(bookingsEndDates[0]);
        endDate = dateFromState(bookingsEndDates[bookingsEndDates.length - 1]);
        const now = new Date();

        if (scope === BOOKINGS_TABS.UPCOMING) {
            // Ensure we're checking from the current time if earliest date is in future
            if (startDate > now) {
                startDate = now;
            }
        } else if (scope === BOOKINGS_TABS.PAST) {
            // Past tab should always refresh from end of current day if the most recent current visit is older
            if (endDate < now) {
                endDate = now;
            }
            endDate = endOfDay(endDate);
        }

        refreshRecordsToGet = (bookingsEndDates.length) ? bookingsEndDates.length : 25;
    }

    try {
        dispatch( setRecordsAreLoading(view, scope, LOADING.REFRESH) );

        let data = await requestData(view, scope, startDate, endDate, direction, refreshRecordsToGet, filters, 0, dateFromState(lastUpdated))

        // HACK - Note that we're passing a per-page of 0 here otherwise the
        // reducer will assume that there's no more records to retrieve,
        // as during a refresh the number of resulting updated records will never
        // be as much as the perPage (which is always the number of records currently loaded.
        dispatch( bookingsLoaded(view, scope, data, direction, false, 0, LOADING.REFRESH) );

        let counts = await requestCountsData(view, scope, filters)
        dispatch( bookingCountsLoaded(view, scope, counts) );
    } catch (error) {
        globalErrorCondition('Error loading bookings data', error);
    }
}

export const cancelBooking = (bookingId, scope, view, updateType = UPDATE.SINGLE) => async (dispatch, getState) => {
    const state = getState();
    const filters = state.module.BOOKINGS.filters;

    try {
        const { data } = await axios.post(`${BOOKINGS_API_URL}booking/${bookingId}/cancel/${updateType}`);
        if (updateType === UPDATE.SINGLE) {
            dispatch( bookingListBookingCancelled(view, scope, data.data.booking) );
        } else {
            dispatch( bookingListBookingSeriesCancelled(view, scope, data.data.bookings) );
        }

        let counts = await requestCountsData(view, scope, filters)
        dispatch( bookingCountsLoaded(view, scope, counts) );

    } catch (error) {
        globalErrorCondition('Error cancelling booking', error);
    }
}

export const deleteBooking = (bookingId, view, updateType = UPDATE.SINGLE) => async (dispatch, getState) => {
    const state = getState();
    const filters = state.module.BOOKINGS.filters;

    try {
        const { data } = await axios.post(`${BOOKINGS_API_URL}booking/${bookingId}/${updateType}`, {
            _method: 'delete'
        });
        let bookingIdsToRemove = (updateType === UPDATE.SINGLE)
            ? [data.data.booking.id]
            : Object.keys(data.data.bookings)

        // Make sure we try and remove the deleted booking(s) from both tabs
        // as we can now find the delete button on either
        dispatch( bookingListRemoveBookings(view, BOOKINGS_TABS.UPCOMING, bookingIdsToRemove) );
        dispatch( bookingListRemoveBookings(view, BOOKINGS_TABS.PAST, bookingIdsToRemove) );

        let counts = await requestCountsData(view, scope, filters)
        dispatch( bookingCountsLoaded(view, scope, counts) );
    } catch (error) {
        globalErrorCondition('Error cancelling booking', error);
    }
}

export const signInBooking = (view, scope, bookingId, wasSearched = null, searchNotes = null) => async (dispatch, getState) => {
    const state = getState();
    const filters = state.module.BOOKINGS.filters;

    try {
        const { data } = await axios.post(
            `${BOOKINGS_API_URL}booking/${bookingId}/sign-in`,
            {
                was_searched: wasSearched,
                search_notes: searchNotes
            }
        );
        dispatch(bookingListUpdated(view, scope, data.booking));
        dispatch(hideModal(SEARCHED_AT_SIGN_IN_MODAL));

        let counts = await requestCountsData(view, scope, filters)
        dispatch( bookingCountsLoaded(view, scope, counts) );
    } catch (error) {
        globalErrorCondition('Error signing in booking', error);
    }
}

export const signOutBooking = (view, scope, bookingId) => async (dispatch, getState) => {
    const state = getState();
    const filters = state.module.BOOKINGS.filters;

    try {
        const { data } = await axios.post(`${BOOKINGS_API_URL}booking/${bookingId}/sign-out`);
        dispatch(bookingListUpdated(view, scope, data.booking));

        let counts = await requestCountsData(view, scope, filters)
        dispatch( bookingCountsLoaded(view, scope, counts) );
    } catch (error) {
        globalErrorCondition('Error signing out booking', error);
    }
}

export const exportBookings = (scope, view, dateRangeStart, dateRangeEnd, fileType, direction) => async(dispatch, getState) => {
    let state = getState();

    let url = new URL(`/bookings/export`, window.location.origin);

    const multiFilter = state.module.BOOKINGS.filters.multiFilter;
    const categorySlugs = retrieveMultiFilterValuesByPrefix(multiFilter, FILTER_PREFIXES.CATEGORY);
    const blockIds = retrieveMultiFilterValuesByPrefix(multiFilter, FILTER_PREFIXES.BLOCK);

    if (categorySlugs) {
        url.searchParams.set('category_slugs', categorySlugs);
    }

    if (state.module.BOOKINGS.filters.amenity) {
        url.searchParams.set('amenity_ids', [state.module.BOOKINGS.filters.amenity]);
    }

    if (blockIds) {
        url.searchParams.set('block_ids', blockIds);
    }

    url.searchParams.set('type', scope);
    url.searchParams.set('view_own', (view === BOOKINGS_VIEWS.MY_BOOKINGS));
    url.searchParams.set('date_range_start', dateRangeStart);
    url.searchParams.set('date_range_end', dateRangeEnd);
    url.searchParams.set('direction', direction);
    url.searchParams.set('per_page', 100000);
    url.searchParams.set('search_term', state.module.BOOKINGS.filters.searchTerm ?? '');
    url.searchParams.set('status', state.module.BOOKINGS.filters.status ?? '');

    url.searchParams.set('file_type', fileType);

    window.open(url.toString(), '_blank');
}


export const getCategorySummaries = () => async (dispatch, getState) => {
    try {
        const result = await axios.get(`${BOOKINGS_API_URL}categories`);
        dispatch( categorySummariesLoaded(result.data.categories) );
    } catch (error) {
        globalErrorCondition('Error loading booking category summaries data', error);
    }
}
